import React, { useEffect, useRef, useState } from 'react';
import s from './CanvasOverlayActions.module.css';
import IconDelete from '../../../../shared/ui/icons/IconDelete';
import Button from '../../../../shared/ui/button/Button';
import { motion } from 'framer-motion';
import { AnimatedScale } from '../../../../shared/ui/animated-container/AnimatedScale';
import { toast } from 'react-toastify';
import { deleteImageFromLayout, updateImageLayout } from '../../../../shared/api/imageLayoutApi';
import { useAppDispatch } from '../../../../store/store';

import { useSelector } from 'react-redux';
import {
    selectImageLayout,
    selectImageProject,
    selectSelectedImage,
} from '../../../../store/selectors/imageSelectors';
import { deleteText, setSelectedImage, setSelectedText } from '../../../../store/slices/imageSlice';
import IconDownload from '../../../../shared/ui/icons/IconDownload';
import {
    setActiveSettingsSlider,
    setPricingModal,
    toggleActivePrompt,
} from '../../../../store/slices/pageSlice';
import { selectActivePrompt } from '../../../../store/selectors/pageSelectors';
import IconEnhance from '../../../../shared/ui/icons/IconEnhance';
import {
    downloadLayoutImage,
    enhanceImageUrl,
    removeBackgroundImageUrl,
    updateLayoutImage,
} from '../../../../shared/api/imageProjectApi';
import IconRemoveBackground from '../../../../shared/ui/icons/IconRemoveBackground';
import { uploadCanvasImage } from '../../../../shared/helpers/downloadCanvas';
import { useCanvasContainer } from '../../../../shared/context/CanvasContainerContext';
import { setLoadingDownload } from '../../../../store/slices/imageSlice';
import IconCrop from '../../../../shared/ui/icons/IconCrop';
import { useLocation } from 'react-router-dom';
import { detachVideoFromProject } from '../../../../shared/api/videoProjectApi';
import { selectCurrentProject } from '../../../../store/selectors/projectSelectors';
import {
    selectIsBasicSubscription,
    selectIsFreeSubscription,
} from '../../../../store/selectors/subscriptionSelector';
import IconLock from '../../../../shared/ui/icons/IconLock';

interface CanvasOverlayActionsProps {
    onStartCrop?: () => void;
    projectVideo?: {
        id: string;
        url: string;
        filename: string;
    } | null;
}

const CanvasOverlayActions: React.FC<CanvasOverlayActionsProps> = ({
    onStartCrop,
    projectVideo,
}) => {
    const [showMenu, setShowMenu] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const dispatch = useAppDispatch();

    const selectedImage = useSelector(selectSelectedImage);
    const imageLayout = useSelector(selectImageLayout);
    const activePrompt = useSelector(selectActivePrompt);
    const canvas = useCanvasContainer();
    const activeLayout = useSelector(selectImageLayout);
    const texts = useSelector(selectImageLayout)?.inline_text;
    const imageData = useSelector(selectImageProject);
    const location = useLocation();
    const currentProject = useSelector(selectCurrentProject);

    const isFreeSubscription = useSelector(selectIsFreeSubscription);
    const isBasicSubscription = useSelector(selectIsBasicSubscription);

    useEffect(() => {
        if (!showMenu) return;
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setShowMenu(false);
                setShowConfirm(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showMenu]);

    const handleDelete = async () => {
        if (projectVideo?.id && currentProject?.id) {
            try {
                await dispatch(detachVideoFromProject({ projectId: currentProject.id }));
                toast.success('Video detached successfully');
            } catch (error: any) {
                toast.error(error?.message || 'Failed to detach video');
            } finally {
                setShowConfirm(false);
                setShowMenu(false);
            }

            return;
        }

        if (selectedImage.image && imageLayout?.id) {
            try {
                await dispatch(
                    deleteImageFromLayout({
                        layoutId: imageLayout.id,
                        imageId: selectedImage.image.id,
                    }),
                ).unwrap();
                await handleDeleteText();
                if (location.pathname.includes('/custom-post')) {
                    dispatch(toggleActivePrompt(null));
                }

                dispatch(setSelectedImage({ image: null, layoutId: null }));
                toast.success('Image deleted successfully');
            } catch (error: any) {
                toast.error(error?.message || 'Failed to delete image');
            } finally {
                setShowConfirm(false);
                setShowMenu(false);
            }
        }
    };

    const handleEnhance = async () => {
        if (isFreeSubscription || isBasicSubscription) {
            dispatch(setPricingModal(true));
            return;
        }
        try {
            console.log('Enhancing image...');
            if (selectedImage?.image?.id) {
                await dispatch(enhanceImageUrl({ imageId: selectedImage?.image?.id }));
            }

            toast.success('Image enhanced successfully');
        } catch (error: any) {
            console.error('Failed to enhance image:', error);
            toast.error(error?.message || 'Failed to enhance image');
        }
    };
    const handleRemoveBG = async () => {
        if (isFreeSubscription || isBasicSubscription) {
            dispatch(setPricingModal(true));
            return;
        }
        if (!selectedImage?.image?.id) return;
        try {
            await dispatch(removeBackgroundImageUrl({ imageId: selectedImage.image.id }));
            toast.success('Image background removed successfully');
        } catch (error: any) {
            toast.error(error?.message || 'Failed to remove image background');
        }
    };
    const handleDownload = async () => {
        dispatch(setLoadingDownload(true));
        try {
            if (canvas.current && activeLayout?.id) {
                const canvasEl = canvas.current.querySelector('canvas') as HTMLCanvasElement;
                if (canvasEl) {
                    const file = await uploadCanvasImage(canvas);
                    if (file) {
                        await dispatch(
                            updateLayoutImage({ layoutId: activeLayout.id, file }),
                        ).unwrap();
                    }
                }
                await dispatch(
                    downloadLayoutImage({
                        layoutId: activeLayout.id,
                        watermark: true,
                        ext: 'png',
                    }),
                ).unwrap();
            }

            toast.success('Image downloaded successfully');
        } catch (error) {
            console.error('Failed to download image:', error);
            toast.error('Failed to download image');
        } finally {
            dispatch(setLoadingDownload(false));
        }
    };

    const handleDeleteText = async () => {
        if (texts && texts.length > 0) {
            dispatch(deleteText(texts[0].id));

            if (imageLayout && imageData) {
                dispatch(setSelectedText(null));

                const newImageLayout = {
                    ...imageLayout,
                    inline_text: [],
                };
                dispatch(
                    updateImageLayout({
                        imageProjectId: imageData.id,
                        layoutId: imageLayout.id,
                        layout: newImageLayout,
                    }),
                );
            }
        }
    };

    if (!selectedImage.image && !projectVideo?.id) return null;

    return (
        <motion.div
            ref={containerRef}
            className={s.container}
            style={{}}
            animate={{
                backgroundColor: showMenu ? 'rgba(255, 255, 255, 0.34)' : 'rgba(241, 224, 224, 0)',
            }}
            transition={{ duration: 0.3 }}
            initial={false}
            exit={{ backgroundColor: 'rgba(241, 224, 224, 0)' }}
        >
            {activePrompt === 'adjust_colors' && (
                <button className={s.enchance_button} onClick={handleEnhance}>
                    {isFreeSubscription || isBasicSubscription ? (
                        <IconLock color="white" />
                    ) : (
                        <IconEnhance color="white" width={20} height={20} />
                    )}
                    Enhance
                </button>
            )}
            {activePrompt === 'image_wizard' && (
                <button className={s.remove_background_button} onClick={handleRemoveBG}>
                    {isFreeSubscription || isBasicSubscription ? (
                        <IconLock color="black" />
                    ) : (
                        <IconRemoveBackground width={20} height={20} />
                    )}
                    Remove Background
                </button>
            )}

            {!showMenu && !showConfirm && (
                <button
                    className={s.dotsBtn}
                    onClick={() => {
                        // dispatch(toggleActivePrompt(null));
                        dispatch(setActiveSettingsSlider(null));
                        setShowMenu(true);
                    }}
                    aria-label="More actions"
                >
                    <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <circle cx="5" cy="12" r="2" fill="white" />
                        <circle cx="12" cy="12" r="2" fill="white" />
                        <circle cx="19" cy="12" r="2" fill="white" />
                    </svg>
                </button>
            )}
            {showMenu && !projectVideo?.id && (
                <div className={s.actionBtns}>
                    <button
                        className={s.downloadBtn}
                        onClick={() => {
                            if (onStartCrop) {
                                onStartCrop();
                                setShowMenu(false);
                            }
                        }}
                    >
                        <IconCrop width={24} height={24} />
                    </button>
                    <button className={s.downloadBtn} onClick={() => handleDownload()}>
                        <IconDownload width={24} height={24} />
                    </button>
                </div>
            )}
            {showMenu && (
                <motion.div
                    className={s.menuBox}
                    initial={{ opacity: 0, y: 100 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 100, transition: { duration: 0.3 } }}
                    transition={{ duration: 0.3 }}
                >
                    <button className={s.deleteBtn} onClick={() => setShowConfirm(!showConfirm)}>
                        <IconDelete
                            width={24}
                            height={24}
                            color={showConfirm ? '#F21616' : '#2E2E2E'}
                        />
                    </button>

                    {showConfirm && (
                        <AnimatedScale className={s.confirmBox}>
                            <div className={s.confirmText}>Confirm delete?</div>
                            <div className={s.confirmActions}>
                                <Button
                                    text="Cancel"
                                    style={{ width: '100px' }}
                                    onClick={() => setShowConfirm(false)}
                                />
                                <Button
                                    text="Yes"
                                    style={{
                                        backgroundColor: '#F21616',
                                        color: '#fff',
                                        width: '100px',
                                    }}
                                    onClick={handleDelete}
                                />
                            </div>
                        </AnimatedScale>
                    )}
                </motion.div>
            )}
        </motion.div>
    );
};

export default CanvasOverlayActions;
